import { z } from 'zod'
import { PromotionSchema } from '@reward-platform/shared-schemas'

export const PaymentOptionTypeSchema = z.enum(['FULL_CASH', 'PART_AVIOS_PART_CASH', 'FULL_AVIOS'])

export const PricedPaymentOptionSchema = z.object({
  id: z.string(),
  type: PaymentOptionTypeSchema,
  cashPrice: z.number().optional(),
  aviosPrice: z.number().optional(),
  collectAmount: z.number().optional(),
  cashPriceWithSurcharges: z.number().optional(),
  promotions: z.array(PromotionSchema.partial()),
})

export type PaymentOptionType = z.infer<typeof PaymentOptionTypeSchema>
export type PricedPaymentOption = z.infer<typeof PricedPaymentOptionSchema>

export const BADGE_PROMOTION_TYPES = ['BONUS_AVIOS', 'ENHANCED_EARN_RATE'] as const

type PartialPromotion = PricedPaymentOption['promotions'][number]

export const getPaymentOptionByType = (
  paymentOptions: PricedPaymentOption[] | undefined,
  type: PaymentOptionType
): PricedPaymentOption | undefined => paymentOptions?.find((option) => option.type === type)

export const getPromotionsForOptionType = (
  paymentOptions: PricedPaymentOption[] | undefined,
  type: PaymentOptionType
): PartialPromotion[] => getPaymentOptionByType(paymentOptions, type)?.promotions ?? []

export const getFullCashPromotions = (
  paymentOptions: PricedPaymentOption[] | undefined
): PartialPromotion[] | undefined => {
  const promotions = getPromotionsForOptionType(paymentOptions, 'FULL_CASH')
  return promotions.length > 0 ? promotions : undefined
}

export const getBadgePromotionsForSummaryDisplay = (
  paymentOptions: PricedPaymentOption[] | undefined
): PartialPromotion[] => {
  if (!paymentOptions?.length) return []

  const seen = new Set<string>()
  const collected: PartialPromotion[] = []

  paymentOptions.forEach(({ promotions }) => {
    promotions.forEach((promotion) => {
      if (
        !promotion.type ||
        !BADGE_PROMOTION_TYPES.includes(promotion.type as (typeof BADGE_PROMOTION_TYPES)[number])
      ) {
        return
      }
      if (seen.has(promotion.type)) return
      seen.add(promotion.type)
      collected.push(promotion)
    })
  })

  return collected
}

/** Merges FULL_CASH promotions (incl. strikethrough) with badge promos from all payment options. */
export const getPromotionsForCardDisplay = (
  paymentOptions: PricedPaymentOption[] | undefined
): PartialPromotion[] | undefined => {
  if (!paymentOptions?.length) return undefined

  const merged = [...getPromotionsForOptionType(paymentOptions, 'FULL_CASH')]
  const seenTypes = new Set(merged.map(({ type }) => type).filter(Boolean) as string[])

  getBadgePromotionsForSummaryDisplay(paymentOptions).forEach((promotion) => {
    if (!promotion.type || seenTypes.has(promotion.type)) return
    seenTypes.add(promotion.type)
    merged.push(promotion)
  })

  return merged.length > 0 ? merged : undefined
}

type PricingCarrier = {
  paymentOptions?: PricedPaymentOption[]
  roomRates?: PricingCarrier[]
  timesAndPrices?: PricingCarrier[]
}

const collectPaymentOptionsFromCarrier = (
  carrier: PricingCarrier,
  collected: PricedPaymentOption[]
): void => {
  carrier.paymentOptions?.forEach((option) => collected.push(option))
  carrier.roomRates?.forEach((roomRate) => collectPaymentOptionsFromCarrier(roomRate, collected))
  carrier.timesAndPrices?.forEach((entry) => collectPaymentOptionsFromCarrier(entry, collected))
}

export const collectPaymentOptionsFromCarriers = (
  carriers: unknown[] | undefined
): PricedPaymentOption[] => {
  if (!carriers) return []

  const collected: PricedPaymentOption[] = []

  carriers.forEach((item) => {
    if (!item || typeof item !== 'object') return
    collectPaymentOptionsFromCarrier(item as PricingCarrier, collected)
  })

  return collected
}

export const getBadgePromotionsFromCarriers = (
  carriers: unknown[] | undefined
): PartialPromotion[] =>
  getBadgePromotionsForSummaryDisplay(collectPaymentOptionsFromCarriers(carriers))
